// Owner editor — six sections matching docs/mockups/profile-edit.html.
// Auto-saved on blur via PUT /api/v1/me; links via PUT /me/links.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api/client";
import { getMe, replaceMyLinks, updateMe } from "../api/profile";
import { STYLING_SECTIONS } from "../api/types";
import type { LinkOut, ProfileOut, ProfileUpdate } from "../api/types";
import { BusinessCard, CARD_STYLES, type CardStyle, type CardUser } from "../components/BusinessCard";
import { CardStylePicker } from "../components/CardStylePicker";
import { Chrome } from "../components/Chrome";
import { ImageUpload } from "../components/ImageUpload";
import { SectionHead } from "../components/SectionHead";
import { StylingSection } from "../components/StylingSection";

const KNOWN_STYLES = new Set<string>(CARD_STYLES.map((s) => s.value));
function asCardStyle(v: unknown): CardStyle {
  return typeof v === "string" && KNOWN_STYLES.has(v) ? (v as CardStyle) : "editorial";
}

type LinkDraft = Omit<LinkOut, "id"> & { id?: number };

export function EditPage() {
  const [profile, setProfile] = useState<ProfileOut | null>(null);
  const [draft, setDraft] = useState<ProfileUpdate>({});
  const [links, setLinks] = useState<LinkDraft[]>([]);
  const [status, setStatus] = useState<"idle" | "loading" | "saving" | "saved" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const me = await getMe();
        setProfile(me);
        setLinks(me.links ?? []);
        setStatus("idle");
      } catch (err) {
        setError(err instanceof ApiError ? err.detail : "Couldn't load your card");
        setStatus("error");
      }
    })();
  }, []);

  const patch = useCallback(
    (patchObj: ProfileUpdate) => setDraft((d) => ({ ...d, ...patchObj })),
    [],
  );

  const fieldProps = useCallback(
    <K extends keyof ProfileUpdate>(key: K) => ({
      value: ((draft[key] as string | null | undefined) ??
        (profile?.[key] as string | null | undefined) ??
        "") as string,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        patch({ [key]: e.target.value || null } as ProfileUpdate),
    }),
    [draft, profile, patch],
  );

  async function save() {
    if (!profile) return;
    setStatus("saving");
    setError(null);
    try {
      const updated = await updateMe(draft);
      const refreshed = await replaceMyLinks(
        links.map(({ type, link, value, position }) => ({ type, link, value, position })),
      );
      setProfile({ ...updated, links: refreshed });
      setDraft({});
      setLinks(refreshed);
      setStatus("saved");
      setTimeout(() => setStatus("idle"), 2400);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Save failed");
      setStatus("error");
    }
  }

  if (status === "loading") {
    return (
      <>
        <Chrome />
        <main className="grid min-h-[40vh] place-items-center">
          <span className="eyebrow">loading your card…</span>
        </main>
      </>
    );
  }

  if (!profile) {
    return (
      <>
        <Chrome />
        <main className="mx-auto max-w-2xl p-7">
          <p className="text-plum">{error ?? "Profile unavailable"}</p>
        </main>
      </>
    );
  }

  return (
    <>
      <Chrome />
      <main className="mx-auto max-w-5xl px-7 pb-32 pt-9">
        <p className="eyebrow eyebrow-accent">Sections</p>
        <h1 className="mt-3 text-5xl tracking-tight">
          Edit your card{profile.first_name ? `, ${profile.first_name}.` : "."}
        </h1>

        {/* 01 Identity */}
        <SectionHead
          num="01"
          title="Identity"
          lede={
            <>
              Your URL: <span className="font-mono text-terra">varo.biz/{profile.username}</span>
            </>
          }
        />
        <div className="rounded-lg border border-rule-soft bg-paper-deep p-6">
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="First name" id="first_name" {...fieldProps("first_name")} />
            <Field label="Last name" id="last_name" {...fieldProps("last_name")} />
            <Field label="Phone" id="phone" {...fieldProps("phone")} />
            <Field label="Website" id="web" type="url" {...fieldProps("web")} />
            <Field label="Address" id="address" {...fieldProps("address")} />
            <Field label="Designation" id="designation" {...fieldProps("designation")} />
          </div>
        </div>

        {/* Content */}
        <SectionHead
          num="·"
          title="Content"
          lede="Headline, about copy, and the YouTube link for the video section."
        />
        <div className="rounded-lg border border-rule-soft bg-paper-deep p-6 space-y-5">
          <Field label="About heading" id="about_heading" {...fieldProps("about_heading")} />
          <Textarea label="About text" id="about_text" {...fieldProps("about_text")} rows={5} />
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Video heading" id="video_heading" {...fieldProps("video_heading")} />
            <Field
              label="Video URL (YouTube)"
              id="video_url"
              type="url"
              {...fieldProps("video_url")}
            />
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <ImageUpload
              label="Profile photo"
              purpose="profile"
              value={(draft.profile as string | null | undefined) ?? profile.profile}
              onChange={(url) => patch({ profile: url })}
              helper="Square crop works best (480×480 or larger)."
            />
            <ImageUpload
              label="Card logo"
              purpose="card-logo"
              value={(draft.card_logo as string | null | undefined) ?? profile.card_logo}
              onChange={(url) => patch({ card_logo: url })}
              helper="SVG or transparent PNG preferred."
            />
          </div>
        </div>

        {/* 02-05 Styling sections; card_ section also gets the style picker row */}
        {STYLING_SECTIONS.map((s) => {
          const merged = { ...profile, ...draft } as ProfileUpdate;
          return (
            <div key={s.key || "main"}>
              <StylingSection
                prefix={s.key}
                num={s.num}
                title={s.label}
                draft={merged}
                onChange={patch}
              />
              {s.key === "card_" && (
                <CardStyleRow
                  current={asCardStyle(draft.card_style ?? profile.card_style)}
                  onOpen={() => setPickerOpen(true)}
                  user={profile as CardUser}
                />
              )}
            </div>
          );
        })}

        {/* 06 Social Links */}
        <SectionHead num="06" title="Social links" lede="Drag to reorder (coming). Up to 12." />
        <div className="space-y-3">
          {links.map((link, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[160px_1fr_28px] items-center gap-3 rounded border border-rule bg-cream-card p-3"
            >
              <select
                className="field-input"
                value={link.value ?? ""}
                onChange={(e) =>
                  setLinks(links.map((l, i) => (i === idx ? { ...l, value: e.target.value } : l)))
                }
              >
                <option value="">— platform —</option>
                {["Instagram", "Facebook", "LinkedIn", "Twitter", "YouTube", "Read.cv", "Website"].map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
              <input
                type="url"
                className="field-input"
                placeholder="https://…"
                value={link.link ?? ""}
                onChange={(e) =>
                  setLinks(links.map((l, i) => (i === idx ? { ...l, link: e.target.value } : l)))
                }
              />
              <button
                type="button"
                aria-label="Remove"
                onClick={() => setLinks(links.filter((_, i) => i !== idx))}
                className="text-ink-mute hover:text-plum"
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() =>
              setLinks([
                ...links,
                { type: "social", value: null, link: null, position: links.length },
              ])
            }
          >
            + Add social link
          </button>
        </div>

        {/* Sticky save bar */}
        <div className="fixed inset-x-0 bottom-4 z-40 mx-auto flex max-w-3xl items-center gap-4 rounded-full border border-rule bg-cream-card px-5 py-3 shadow-lg">
          <span
            className={`flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] ${
              status === "saved" ? "text-moss" : status === "error" ? "text-plum" : "text-ink-mute"
            }`}
          >
            <span
              className={`inline-block size-1.5 rounded-full ${
                status === "saved" ? "bg-moss" : status === "error" ? "bg-plum" : "bg-ink-mute"
              }`}
            />
            {status === "saving" && "Saving…"}
            {status === "saved" && "All changes saved"}
            {status === "error" && (error ?? "Error")}
            {status === "idle" && (Object.keys(draft).length > 0 ? "Unsaved changes" : "Up to date")}
          </span>
          <div className="ml-auto flex gap-2">
            <Link
              to={`/${profile.username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost text-xs"
            >
              Preview public ↗
            </Link>
            <button
              type="button"
              onClick={save}
              disabled={status === "saving" || Object.keys(draft).length === 0 && links === profile.links}
              className="btn btn-primary text-xs"
            >
              Save
            </button>
          </div>
        </div>
      </main>

      <CardStylePicker
        open={pickerOpen}
        current={asCardStyle(draft.card_style ?? profile.card_style)}
        user={profile as CardUser}
        onSelect={(style) => {
          patch({ card_style: style });
          setPickerOpen(false);
        }}
        onClose={() => setPickerOpen(false)}
      />
    </>
  );
}

interface CardStyleRowProps {
  current: CardStyle;
  user: CardUser;
  onOpen: () => void;
}
function CardStyleRow({ current, user, onOpen }: CardStyleRowProps) {
  const meta = CARD_STYLES.find((s) => s.value === current);
  return (
    <div className="mb-9 -mt-2 rounded-lg border border-rule bg-paper-deep p-5">
      <div className="flex items-center gap-5">
        <div className="grid h-28 w-44 shrink-0 place-items-center overflow-hidden rounded border border-rule bg-paper">
          <BusinessCard variant={current} user={user} size="thumb" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="eyebrow eyebrow-accent">Card design</p>
          <div className="mt-1.5 flex items-baseline gap-3">
            <span className="font-display text-2xl tracking-tight">
              {meta?.label ?? "Editorial Paper"}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-mute">
              {meta?.tag ?? "Literary"}
            </span>
          </div>
          <p className="mt-1 text-sm text-ink-mute">{meta?.blurb}</p>
        </div>
        <button
          type="button"
          onClick={onOpen}
          className="btn btn-secondary shrink-0 text-xs"
        >
          Browse all 10 →
        </button>
      </div>
    </div>
  );
}

interface FieldP {
  label: string;
  id: string;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}
function Field({ label, id, type = "text", placeholder, value, onChange }: FieldP) {
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        className="field-input"
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

interface TextareaP {
  label: string;
  id: string;
  value: string;
  rows?: number;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}
function Textarea({ label, id, value, rows = 4, onChange }: TextareaP) {
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <textarea id={id} rows={rows} className="field-input" value={value} onChange={onChange} />
    </div>
  );
}
